
import { useState, useCallback, useRef } from 'react';
import { MessageType } from '@/types/assistant';
import { logger } from '@/utils/production-logger';
import { MessageService } from '@/services/MessageService';
import { DatabaseStreamingService } from '@/services/DatabaseStreamingService';

interface UseMessageStateAndPersistenceProps {
  initialMessages?: MessageType[];
  onMessagesChange?: (messages: MessageType[]) => void;
  onSaveError?: (error: Error, messageId: string, requestId?: string) => void;
}

interface UseMessageStateAndPersistenceReturn {
  messages: MessageType[];
  setMessages: React.Dispatch<React.SetStateAction<MessageType[]>>;
  clearMessages: () => void;
  addUserMessage: (content: string, sessionId: string, userId: string, autoSave?: boolean) => Promise<MessageType>;
  addAiMessage: (aiMessage: MessageType, autoSave?: boolean) => Promise<void>;
  addFallbackMessage: (fallbackMessage: MessageType, autoSave?: boolean) => Promise<void>;
  removeMessage: (messageId: string) => void;
  updateMessage: (messageId: string, updates: Partial<MessageType>) => void;
  saveMessage: (message: MessageType, requestId?: string) => Promise<boolean>;
}

export const useMessageStateAndPersistence = ({
  initialMessages = [],
  onMessagesChange,
  onSaveError
}: UseMessageStateAndPersistenceProps = {}): UseMessageStateAndPersistenceReturn => {
  
  const [messages, setMessages] = useState<MessageType[]>(initialMessages);

  // STEP 4: Track active save requests to prevent duplicates
  const activeSaveRequests = useRef(new Set<string>());

  // SIMPLIFIED save function - using DatabaseStreamingService only
  const saveMessage = useCallback(async (
    message: MessageType, 
    requestId?: string
  ): Promise<boolean> => {
    try {
      console.log('[HOOK] saveMessage called with:', {
        messageId: message.id,
        sessionId: message.session_id,
        userId: message.user_id,
        role: message.role,
        isStreaming: message.is_streaming,
        requestId
      });

      // STEP 4: Check if save request is already active
      const saveKey = `${message.id}-${requestId || 'default'}`;
      if (activeSaveRequests.current.has(saveKey)) {
        console.log('[HOOK] Save request already active, skipping:', { saveKey });
        return true;
      }

      // Skip streaming messages
      if (message.is_streaming) {
        console.log('[HOOK] Skipping streaming message save');
        return true;
      }

      // STEP 4: Track this save request
      activeSaveRequests.current.add(saveKey);

      try {
        console.log('[HOOK] Calling DatabaseStreamingService.saveMessageWithStreamingValidation');
        const saveResult = await DatabaseStreamingService.saveMessageWithStreamingValidation(message);
        
        console.log('[HOOK] Save result:', { saveResult, messageId: message.id });

        if (!saveResult) {
          const error = new Error('Failed to save message');
          console.error('[HOOK] Save failed, calling onSaveError');
          onSaveError?.(error, message.id, requestId);
          return false;
        }

        console.log('[HOOK] Message saved successfully');
        return true;
      } finally {
        // STEP 4: Clean up save request tracking
        activeSaveRequests.current.delete(saveKey);
      }
    } catch (error) {
      // STEP 4: Clean up save request tracking on error
      const saveKey = `${message.id}-${requestId || 'default'}`;
      activeSaveRequests.current.delete(saveKey);

      console.error('[HOOK] Save error caught:', {
        error: error instanceof Error ? error.message : String(error),
        messageId: message.id
      });
      const saveError = error instanceof Error ? error : new Error(String(error));
      onSaveError?.(saveError, message.id, requestId);
      return false;
    }
  }, [onSaveError]);

  const addUserMessage = useCallback(async (
    content: string, 
    sessionId: string, 
    userId: string, 
    autoSave: boolean = true
  ): Promise<MessageType> => {
    console.log('[HOOK] addUserMessage called with:', {
      contentLength: content.length,
      sessionId,
      userId,
      autoSave
    });

    const newMessage = MessageService.createUserMessage(content, sessionId, userId);
    
    console.log('[HOOK] User message created:', {
      messageId: newMessage.id,
      sessionId: newMessage.session_id,
      userId: newMessage.user_id
    });

    // IMMEDIATE state update
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, newMessage];
      onMessagesChange?.(updatedMessages);
      return updatedMessages;
    });

    console.log('[HOOK] User message added to state');

    // ASYNC save without blocking
    if (autoSave) {
      console.log('[HOOK] Starting async save for user message');
      saveMessage(newMessage).catch(error => {
        console.error('[HOOK] Failed to save user message:', {
          error: error instanceof Error ? error.message : String(error),
          messageId: newMessage.id
        });
        logger.error('Failed to save user message', {
          error: error instanceof Error ? error.message : String(error),
          messageId: newMessage.id
        });
      });
    }

    return newMessage;
  }, [onMessagesChange, saveMessage]);

  const addAiMessage = useCallback(async (
    aiMessage: MessageType, 
    autoSave: boolean = true
  ) => {
    // IMMEDIATE state update
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, aiMessage];
      onMessagesChange?.(updatedMessages);
      return updatedMessages;
    });

    // ASYNC save only for non-streaming messages
    if (autoSave && !aiMessage.is_streaming) {
      saveMessage(aiMessage).catch(error => {
        logger.error('Failed to save AI message', {
          error: error instanceof Error ? error.message : String(error),
          messageId: aiMessage.id
        });
      });
    }
  }, [onMessagesChange, saveMessage]);

  const addFallbackMessage = useCallback(async (
    fallbackMessage: MessageType, 
    autoSave: boolean = true
  ) => {
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, fallbackMessage];
      onMessagesChange?.(updatedMessages);
      return updatedMessages;
    });

    if (autoSave) {
      saveMessage(fallbackMessage).catch(error => {
        logger.error('Failed to save fallback message', {
          error: error instanceof Error ? error.message : String(error),
          messageId: fallbackMessage.id
        });
      });
    }
  }, [onMessagesChange, saveMessage]);

  // STEP 3: Remove auto-save from updateMessage
  const updateMessage = useCallback((messageId: string, updates: Partial<MessageType>) => {
    console.log('[HOOK] updateMessage called:', {
      messageId,
      updates: Object.keys(updates),
      isStreamingUpdate: 'is_streaming' in updates
    });

    setMessages(prevMessages => {
      const updatedMessages = prevMessages.map(msg => {
        if (msg.id === messageId) {
          const updatedMsg = { ...msg, ...updates };
          
          // STEP 3: REMOVED auto-save when streaming completes
          // No longer auto-save here to prevent duplicate saves
          console.log('[HOOK] Message updated in state (no auto-save):', {
            messageId,
            wasStreaming: msg.is_streaming,
            nowStreaming: updatedMsg.is_streaming
          });
          
          return updatedMsg;
        }
        return msg;
      });
      onMessagesChange?.(updatedMessages);
      return updatedMessages;
    });
  }, [onMessagesChange]);

  const removeMessage = useCallback((messageId: string) => {
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.filter(msg => msg.id !== messageId);
      onMessagesChange?.(updatedMessages);
      return updatedMessages;
    });
  }, [onMessagesChange]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    onMessagesChange?.([]);
  }, [onMessagesChange]);

  return {
    messages,
    setMessages,
    clearMessages,
    addUserMessage,
    addAiMessage,
    addFallbackMessage,
    removeMessage,
    updateMessage,
    saveMessage
  };
};
